import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router';
import type { VpsDTO } from '@devrobot/shared';
import { Cpu, HardDrive, MemoryStick, Plus, Search, SearchX, Server } from 'lucide-react';
import { useVpsList } from '../../api/queries';
import { useUser } from '../../api/user';
import { CapacityBars } from '../../components/CapacityBars';
import { EmptyState } from '../../components/EmptyState';
import { ErrorAlert } from '../../components/ErrorAlert';
import { PageHeader } from '../../components/PageHeader';
import { RelativeTime } from '../../components/RelativeTime';
import { SkeletonTable } from '../../components/Skeleton';
import { StatTile } from '../../components/StatTile';
import { StatusBadge } from '../../components/StatusBadge';
import { listTransition } from '../../kit/hooks';
import { formatBytes, percent } from '../../lib/format';
import { DOT_CLASS, statusInfo } from '../../lib/status';

type Filter = 'all' | 'connected' | 'attention';

const FILTERS: [Filter, string][] = [
  ['all', 'All'],
  ['connected', 'Connected'],
  ['attention', 'Needs attention'],
];

const vtName = (id: string) => ({ '--vt': `vps-${id.replace(/[^a-zA-Z0-9_-]/g, '-')}` }) as CSSProperties;
const needsAttention = (v: VpsDTO) => v.connection !== 'connected' || v.enrollmentState !== 'ready' || v.deploymentsDisabled || v.maintenance;

/** Connection state as a dot and its label; the dot pings while the agent is live. */
function ConnectionDot({ value }: { value: VpsDTO['connection'] }) {
  const info = statusInfo('connection', value);
  const cls = `${DOT_CLASS[info.tone]} status-md`;
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap" title={info.hint}>
      {value === 'connected' ? (
        <span className="inline-grid *:[grid-area:1/1]" aria-hidden="true">
          <span className={`${cls} animate-ping`} />
          <span className={cls} />
        </span>
      ) : (
        <span aria-hidden="true" className={cls} />
      )}
      <span>{info.label}</span>
    </span>
  );
}

function Flags({ v }: { v: VpsDTO }) {
  if (!v.deploymentsDisabled && !v.maintenance) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {v.deploymentsDisabled ? <span className="badge badge-warning badge-soft badge-xs">deployments disabled</span> : null}
      {v.maintenance ? <span className="badge badge-warning badge-soft badge-xs">maintenance</span> : null}
    </span>
  );
}

const address = (v: VpsDTO) => `${v.host}${v.port !== 22 ? `:${v.port}` : ''}`;

/** Fleet totals from the servers that report capacity. */
function CapacitySummary({ servers }: { servers: VpsDTO[] }) {
  const observed = servers.filter((v) => v.capacity);
  const connected = servers.filter((v) => v.connection === 'connected').length;
  let cpus = 0;
  let mem = 0;
  let memUsed = 0;
  let disk = 0;
  let diskUsed = 0;
  for (const { capacity: c } of observed) {
    if (!c) continue;
    cpus += c.cpus;
    mem += c.memoryBytes;
    memUsed += c.memoryBytes - c.memoryAvailableBytes;
    disk += c.diskTotalBytes;
    diskUsed += c.diskTotalBytes - c.diskFreeBytes;
  }
  const scope = observed.length === servers.length ? 'across all servers' : `across ${observed.length} of ${servers.length} reporting`;
  return (
    <div className="dr-stagger mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatTile icon={Server} label="Connected" value={connected} detail={`of ${servers.length} server${servers.length === 1 ? '' : 's'}`} />
      <StatTile icon={Cpu} label="CPU cores" value={cpus} detail={scope} />
      <StatTile icon={MemoryStick} label="Memory used" value={percent(memUsed, mem)} format={(n) => `${Math.round(n)}%`} detail={mem ? `${formatBytes(memUsed)} / ${formatBytes(mem)}` : 'not observed'} />
      <StatTile icon={HardDrive} label="Disk used" value={percent(diskUsed, disk)} format={(n) => `${Math.round(n)}%`} detail={disk ? `${formatBytes(disk - diskUsed)} free` : 'not observed'} />
    </div>
  );
}

export default function VpsListPage() {
  const user = useUser();
  const q = useVpsList();
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  // The field updates at once; the list re-flows shortly after, inside a list transition.
  useEffect(() => {
    if (query === appliedQuery) return;
    const t = setTimeout(() => listTransition(() => setAppliedQuery(query)), 160);
    return () => clearTimeout(t);
  }, [query, appliedQuery]);

  const servers = q.data;
  const visible = useMemo(() => {
    const needle = appliedQuery.trim().toLowerCase();
    return (servers ?? []).filter(
      (v) =>
        (filter === 'all' || (filter === 'connected' ? v.connection === 'connected' : needsAttention(v))) &&
        (!needle || `${v.name} ${v.host} ${v.host_info.hostname ?? ''} ${v.host_info.os ?? ''} ${v.domain ?? ''}`.toLowerCase().includes(needle)),
    );
  }, [servers, appliedQuery, filter]);

  return (
    <>
      <PageHeader
        title="VPS"
        subtitle={user.platformAdmin ? 'Managed servers. Each runs a DevRobot agent that connects out to this administrator.' : 'Servers your projects are allowed to deploy to.'}
        actions={
          user.platformAdmin ? (
            <Link to="/vps/new" className="btn btn-primary btn-sm">
              <Plus className="size-4" aria-hidden="true" /> Enroll VPS
            </Link>
          ) : null
        }
      />
      {q.isPending ? <SkeletonTable /> : null}
      {q.isError ? <ErrorAlert error={q.error} onRetry={() => void q.refetch()} /> : null}
      {servers && servers.length === 0 ? (
        <EmptyState
          icon={Server}
          title="No servers yet"
          action={
            user.platformAdmin ? (
              <Link to="/vps/new" className="btn btn-primary btn-sm">
                Enroll your first VPS
              </Link>
            ) : null
          }
        >
          {user.platformAdmin
            ? 'Enrolling connects to the server over SSH once, inspects it, shows you what it will change, and installs the agent after your approval.'
            : 'No VPS is available to your projects yet. Ask a platform administrator to allow your project on a server.'}
        </EmptyState>
      ) : null}
      {servers && servers.length > 0 ? (
        <>
          <CapacitySummary servers={servers} />

          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
            <label className="input w-full lg:w-72">
              <Search className="size-4 opacity-50" aria-hidden="true" />
              <input type="search" data-page-search placeholder="Search servers" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search servers" />
              <kbd className="kbd kbd-sm max-sm:hidden">/</kbd>
            </label>
            <div role="radiogroup" aria-label="Filter by connection" className="tabs tabs-box tabs-sm w-fit border border-hairline">
              {FILTERS.map(([k, label]) => (
                <label key={k} className={`tab ${filter === k ? 'tab-active' : ''}`}>
                  <input type="radio" name="vps-filter" className="sr-only" checked={filter === k} onChange={() => listTransition(() => setFilter(k))} />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {visible.length === 0 ? (
            <EmptyState icon={SearchX} title="No matching servers">
              Try another search or filter.
            </EmptyState>
          ) : (
            <div className="card dr-surface overflow-hidden">
              {/* Phones: stacked rows */}
              <ul className="divide-y divide-hairline md:hidden">
                {visible.map((v) => (
                  <li key={v.id} data-vt style={vtName(v.id)}>
                    <Link to={`/vps/${v.id}`} className="block space-y-2 p-4 hover:bg-base-200/50">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="break-anywhere font-medium">{v.name}</p>
                          <p className="break-anywhere font-mono text-xs text-base-content/60">{address(v)}</p>
                        </div>
                        <StatusBadge kind="enrollment" value={v.enrollmentState} size="xs" />
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                        <ConnectionDot value={v.connection} />
                        <span className="text-xs text-base-content/60">
                          observed <RelativeTime value={v.observedAt} />
                        </span>
                        <span className="text-xs text-base-content/60">
                          {v.targets.length} app{v.targets.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      {v.connection === 'stale' ? <p className="text-xs dr-ink-warning">data shown is not live</p> : null}
                      <Flags v={v} />
                      <CapacityBars capacity={v.capacity} stale={(v.connection === 'stale' || v.connection === 'disconnected') && Boolean(v.capacity)} compact />
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="overflow-x-auto max-md:hidden">
                <table className="table table-sm">
                  <thead>
                    <tr className="text-xs tracking-wide text-base-content/50 uppercase">
                      <th>Server</th>
                      <th>Connection</th>
                      <th>Enrollment</th>
                      <th className="max-lg:hidden">Agent</th>
                      <th className="max-lg:hidden">System</th>
                      <th className="min-w-40">Resources</th>
                      <th>Apps</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((v) => {
                      const stale = v.connection === 'stale' || v.connection === 'disconnected';
                      return (
                        <tr key={v.id} data-vt style={vtName(v.id)} className="align-top hover:bg-base-200/50">
                          <td className="min-w-40">
                            <Link to={`/vps/${v.id}`} className="link link-hover font-medium">
                              {v.name}
                            </Link>
                            <div className="font-mono text-xs text-base-content/60">{address(v)}</div>
                            <div className="mt-1">
                              <Flags v={v} />
                            </div>
                          </td>
                          <td className="min-w-36 text-sm">
                            <ConnectionDot value={v.connection} />
                            <div className="text-xs text-base-content/60">
                              observed <RelativeTime value={v.observedAt} />
                            </div>
                            {v.connection === 'stale' ? <div className="text-xs dr-ink-warning">data shown is not live</div> : null}
                          </td>
                          <td>
                            <StatusBadge kind="enrollment" value={v.enrollmentState} size="xs" />
                          </td>
                          <td className="text-sm max-lg:hidden">
                            {v.agent.version ?? '—'}
                            {v.agent.driver ? <div className="text-xs text-base-content/60">driver {v.agent.driver}</div> : null}
                          </td>
                          <td className="text-sm max-lg:hidden">
                            {v.host_info.os ? `${v.host_info.os} ${v.host_info.osVersion ?? ''}` : '—'}
                            {v.host_info.arch ? <div className="text-xs text-base-content/60">{v.host_info.arch}</div> : null}
                          </td>
                          <td>
                            <CapacityBars capacity={v.capacity} stale={stale && Boolean(v.capacity)} compact />
                          </td>
                          <td className="text-sm tabular-nums">{v.targets.length}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : null}
    </>
  );
}
